import { useState, useEffect } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Ban, Trash2, Plus } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function mondayOf(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - offset);
  return d;
}

export default function AdminCalendar({ ctx }) {
  const { calEvents, calMonth, setCalMonth } = ctx;
  const [weekAnchor, setWeekAnchor] = useState(() => new Date());
  const [includeCanceled, setIncludeCanceled] = useState(false);
  const [busy, setBusy] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [blkDate, setBlkDate] = useState("");
  const [blkNote, setBlkNote] = useState("");
  const [blkSlots, setBlkSlots] = useState([]);
  const [slotOptions, setSlotOptions] = useState([]);

  const shortSlot = (s) => s.split(" - ").map((p) => p.replace(":00", "").replace(/\s?(AM|PM)/i, "")).join("–");

  const loadBlocked = () => api.get("/admin/blocked-dates").then(({ data }) => setBlocked(data)).catch(() => {});
  useEffect(() => {
    loadBlocked();
    api.get("/open-studio/info").then(({ data }) => setSlotOptions(data?.time_slots || [])).catch(() => {});
  }, []);

  const toggleBlkSlot = (s) => setBlkSlots((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const addBlock = async () => {
    if (!blkDate) { toast.error("Pick a date to block"); return; }
    try {
      const { data } = await api.post("/admin/blocked-dates", { date: blkDate, note: blkNote, slots: blkSlots });
      const partial = (data?.slots || []).length > 0;
      toast.success(partial ? "Time slots blocked off" : "Whole day blocked off");
      if (data?.existing_bookings > 0) {
        toast.warning(`Heads up: ${data.existing_bookings} existing booking${data.existing_bookings > 1 ? "s" : ""} in that time stay valid — new bookings are now closed.`);
      }
      setBlkDate(""); setBlkNote(""); setBlkSlots([]);
      loadBlocked();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't block that date"); }
  };
  const removeBlock = async (date) => {
    try { await api.delete(`/admin/blocked-dates/${date}`); loadBlocked(); }
    catch (e) { toast.error("Couldn't unblock"); }
  };

  const blockedMap = {};
  blocked.forEach((b) => { blockedMap[b.date] = { note: b.note || "", slots: b.slots || [] }; });

  const monday = mondayOf(weekAnchor);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const startStr = iso(monday);
  const MONTHS = ["January", "February", "March", "April", "May", "June", "July",
    "August", "September", "October", "November", "December"];
  const rangeLabel = (() => {
    const m1 = MONTHS[monday.getMonth()], m2 = MONTHS[sunday.getMonth()];
    if (monday.getMonth() === sunday.getMonth() && monday.getFullYear() === sunday.getFullYear())
      return `${m1} ${monday.getDate()}–${sunday.getDate()}, ${sunday.getFullYear()}`;
    if (monday.getFullYear() === sunday.getFullYear())
      return `${m1} ${monday.getDate()} – ${m2} ${sunday.getDate()}, ${sunday.getFullYear()}`;
    return `${m1} ${monday.getDate()}, ${monday.getFullYear()} – ${m2} ${sunday.getDate()}, ${sunday.getFullYear()}`;
  })();

  const doExport = async (fmt) => {
    setBusy(fmt);
    try {
      const { data } = await api.get("/admin/weekly-export", {
        params: { start: startStr, format: fmt, include_canceled: includeCanceled },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `weekly-bookings-${startStr}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} weekly schedule downloaded`);
    } catch (e) {
      toast.error("Export failed — please try again.");
    } finally {
      setBusy("");
    }
  };

  const TYPE_STYLE = {
    class: "bg-[#C6FF33] text-[#121212]",
    open_studio: "bg-[#121212] text-[#F2E9D8]",
    party: "bg-[#E0218A] text-white",
  };
  const TYPE_LABEL = { class: "Class", open_studio: "Open Studio", party: "Party" };
  const y = calMonth.getFullYear(), mo = calMonth.getMonth();
  const monthName = calMonth.toLocaleString("default", { month: "long", year: "numeric" });
  const firstDay = new Date(y, mo, 1).getDay();
  const daysInMonth = new Date(y, mo + 1, 0).getDate();
  const byDate = {};
  calEvents.forEach((e) => { const k = e.date.slice(0, 10); (byDate[k] = byDate[k] || []).push(e); });
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const todayKey = new Date().toISOString().slice(0, 10);
  const mondayKey = startStr, sundayKey = iso(sunday);

  const fmtLong = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };

  return (
    <div className="mt-10" data-testid="admin-calendar">
      {/* ---- Weekly export controls ---- */}
      <div data-testid="weekly-export-bar" className="mb-6 bg-white border-2 border-[#121212] p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#121212]">Weekly booking export</p>
            <p className="text-sm text-[#121212]/70 mt-1">Pick any week (or click a day on the calendar), then download the full Mon–Sun schedule.</p>
            <p className="font-serif font-black text-2xl text-[#121212] mt-2" data-testid="export-week-range">Week of {rangeLabel}</p>
          </div>
          <div className="flex flex-col gap-3 items-start">
            <div className="flex items-center gap-2">
              <label className="text-xs tracking-[0.15em] uppercase font-bold">Week</label>
              <input
                data-testid="export-week-picker"
                type="date"
                value={startStr}
                onChange={(e) => { if (e.target.value) setWeekAnchor(new Date(e.target.value + "T00:00:00")); }}
                className="px-3 py-2 border-2 border-[#121212] text-sm focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer" data-testid="export-include-canceled">
              <input type="checkbox" checked={includeCanceled} onChange={(e) => setIncludeCanceled(e.target.checked)} className="w-4 h-4 accent-[#121212]" />
              Include canceled &amp; refunded bookings
            </label>
            <div className="flex gap-2">
              <button
                data-testid="export-csv-btn"
                onClick={() => doExport("csv")}
                disabled={!!busy}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#C6FF33] border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#121212] hover:text-[#C6FF33] transition-colors disabled:opacity-40"
              >
                <FileSpreadsheet size={15} /> {busy === "csv" ? "Preparing…" : "Export CSV"}
              </button>
              <button
                data-testid="export-pdf-btn"
                onClick={() => doExport("pdf")}
                disabled={!!busy}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#121212] text-[#F2E9D8] border-2 border-[#121212] text-xs tracking-[0.2em] uppercase font-bold hover:bg-black transition-colors disabled:opacity-40"
              >
                <FileText size={15} /> {busy === "pdf" ? "Preparing…" : "Export PDF"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Block off dates ---- */}
      <div data-testid="blocked-dates-bar" className="mb-8 bg-white border-2 border-[#B4472F] p-5">
        <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#B4472F] inline-flex items-center gap-2"><Ban size={14} /> Block off dates</p>
        <p className="text-sm text-[#121212]/70 mt-1">Mark days you're unavailable. Block the <strong>whole day</strong> or just <strong>specific time slots</strong>. Blocked times can't be booked for Open Studio (existing bookings stay valid). Notes are private (admin only).</p>
        <div className="flex flex-wrap items-end gap-3 mt-4">
          <div>
            <label className="text-[11px] tracking-[0.15em] uppercase font-bold block mb-1">Date</label>
            <input data-testid="block-date-input" type="date" value={blkDate} onChange={(e) => setBlkDate(e.target.value)} className="px-3 py-2 border-2 border-[#121212] text-sm focus:outline-none" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[11px] tracking-[0.15em] uppercase font-bold block mb-1">Note (private, optional)</label>
            <input data-testid="block-note-input" value={blkNote} onChange={(e) => setBlkNote(e.target.value)} placeholder="e.g. Vacation, Closed, Family event" className="w-full px-3 py-2 border-2 border-stone-200 text-sm focus:outline-none focus:border-[#121212]" />
          </div>
          <button data-testid="block-date-btn" onClick={addBlock} className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#B4472F] text-white border-2 border-[#B4472F] text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#963a26] transition-colors"><Plus size={14} /> {blkSlots.length ? "Block times" : "Block day"}</button>
        </div>
        {slotOptions.length > 0 && (
          <div className="mt-4" data-testid="block-slots">
            <label className="text-[11px] tracking-[0.15em] uppercase font-bold block mb-2">Time slots to block <span className="text-[#121212]/50 normal-case tracking-normal font-normal">— leave all unchecked to block the whole day</span></label>
            <div className="flex flex-wrap gap-2">
              {slotOptions.map((s) => {
                const on = blkSlots.includes(s);
                return (
                  <button key={s} type="button" data-testid={`block-slot-${s.replace(/[: ]/g, "")}`} onClick={() => toggleBlkSlot(s)}
                    className={`px-4 py-2 border-2 text-xs font-bold tracking-[0.05em] transition-colors ${on ? "bg-[#B4472F] text-white border-[#B4472F]" : "bg-white text-[#121212] border-[#121212] hover:bg-[#F7E9E4]"}`}>
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {blocked.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid="blocked-list">
            {blocked.map((b) => (
              <span key={b.date} data-testid={`blocked-${b.date}`} className="inline-flex items-center gap-2 bg-[#F7E9E4] border border-[#B4472F] text-[#B4472F] text-xs font-bold px-3 py-1.5 rounded-full">
                {fmtLong(b.date)} · {(b.slots && b.slots.length) ? b.slots.map(shortSlot).join(", ") : "All day"}{b.note ? ` · ${b.note}` : ""}
                <button data-testid={`unblock-${b.date}`} onClick={() => removeBlock(b.date)} title="Unblock" className="hover:text-[#121212]"><Trash2 size={13} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif font-black text-3xl text-[#121212]">{monthName}</h2>
        <div className="flex gap-2">
          <button data-testid="cal-prev" onClick={() => setCalMonth(new Date(y, mo - 1, 1))} className="px-4 py-2 border-2 border-[#121212] text-sm font-bold hover:bg-[#F2E9D8]">←</button>
          <button data-testid="cal-today" onClick={() => { const t = new Date(); setCalMonth(new Date(t.getFullYear(), t.getMonth(), 1)); }} className="px-4 py-2 border-2 border-[#121212] text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#F2E9D8]">Today</button>
          <button data-testid="cal-next" onClick={() => setCalMonth(new Date(y, mo + 1, 1))} className="px-4 py-2 border-2 border-[#121212] text-sm font-bold hover:bg-[#F2E9D8]">→</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 mb-4 text-xs">
        {Object.entries(TYPE_LABEL).map(([k, l]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className={`w-3 h-3 ${TYPE_STYLE[k]}`} />{l}</span>
        ))}
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-[#B4472F]" />Blocked / Unavailable</span>
      </div>
      <div className="grid grid-cols-7 gap-px bg-[#121212] border-2 border-[#121212]">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="bg-[#F2E9D8] text-center py-2 text-xs tracking-[0.15em] uppercase font-bold">{d}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? `${y}-${pad(mo + 1)}-${pad(d)}` : null;
          const dayEvents = key ? (byDate[key] || []) : [];
          const inWeek = key && key >= mondayKey && key <= sundayKey;
          const isBlocked = key && key in blockedMap;
          const blkInfo = isBlocked ? blockedMap[key] : null;
          const partialBlock = blkInfo && blkInfo.slots && blkInfo.slots.length > 0;
          return (
            <div
              key={i}
              data-testid={d ? `cal-day-${key}` : undefined}
              onClick={d ? () => setWeekAnchor(new Date(key + "T00:00:00")) : undefined}
              style={inWeek && !isBlocked ? { backgroundColor: "#F3FAD6" } : undefined}
              className={`min-h-[110px] p-1.5 ${isBlocked && !partialBlock ? "bg-[#F7E9E4]" : "bg-white"} ${d ? "cursor-pointer hover:bg-[#F2E9D8]" : ""} ${key === todayKey ? "ring-2 ring-[#C6FF33] ring-inset" : ""}`}
            >
              {d && <div className={`text-xs font-bold mb-1 ${isBlocked ? "text-[#B4472F]" : "text-stone-400"}`}>{d}</div>}
              {isBlocked && (
                <div data-testid={`cal-blocked-${key}`} className={`px-1.5 py-1 mb-1 text-[10px] leading-tight font-bold ${partialBlock ? "bg-[#F7E9E4] text-[#B4472F] border border-[#B4472F]" : "bg-[#B4472F] text-white"} inline-flex items-center gap-1 max-w-full`}>
                  <Ban size={10} className="shrink-0" /> <span className="truncate">{partialBlock ? blkInfo.slots.map(shortSlot).join(", ") : "Blocked"}{(!partialBlock && blkInfo.note) ? ` · ${blkInfo.note}` : ""}</span>
                </div>
              )}
              <div className="space-y-1">
                {dayEvents.map((e, j) => (
                  <div key={j} title={`${e.title} · ${e.time} · ${e.detail}`} className={`px-1.5 py-1 text-[10px] leading-tight font-semibold truncate ${TYPE_STYLE[e.type] || "bg-stone-200"}`}>
                    {e.time ? e.time.split(" - ")[0].replace(":00", "") + " " : ""}{e.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {calEvents.length === 0 && <p className="text-stone-500 mt-4">No bookings yet — classes, Open Studio reservations, and party inquiries will appear here automatically.</p>}
    </div>
  );
}
